import React, { useEffect } from "react";
import { KeyboardDatePicker, KeyboardDatePickerProps } from "@material-ui/pickers";

import { useField } from "formik";

import { TextField, TextFieldProps, FormControl, InputLabel, OutlinedInput, FormHelperText } from "@material-ui/core";
import MaskedInput from "react-text-mask";
import { omit } from "lodash";
import createNumberMask from "text-mask-addons/dist/createNumberMask";
import { toProperCase } from "pages/dashboard/interests/utils";

export const CURRENCY_MASK = createNumberMask({ prefix: "$", allowDecimal: true });

type DatePickerProps = Omit<KeyboardDatePickerProps, "onChange" | "value"> & {
  withError?: boolean;
  small?: boolean;
}
const DatePickerField: React.FC<DatePickerProps> = (props) => {
  const { withError, small, label, ...otherProps } = props;
  const [field, meta, helpers] = useField(props as any);
  const { name, value } = field;
  const { touched, error } = meta;
  const { setValue, setTouched, setError } = helpers;
  return (
    <KeyboardDatePicker
      {...otherProps}
      style={{ minWidth: "15em" }}
      error={touched && !!error}
      margin={small ? "dense" : "normal"}
      id={`hc-date-${name}`}
      name={name}
      autoComplete="off"
      autoOk
      variant="inline"
      inputVariant="outlined"
      label={label ?? ""}
      format="MM/dd/yyyy"
      value={value}
      InputAdornmentProps={{ position: "end" }}
      onClick={() => setTouched(true)}
      helperText={withError && touched && error}
      onChange={(date: Date | null) => {
        if (date && isNaN(date.getDay())) {
          setError("Invalid Date");
        } else {
          setValue(date);
        }
      }}
    />
  );
};

export type InputWithErrorLabelProps = {
  label?: string;
  name: string;
} & TextFieldProps;

const Input: React.FC<InputWithErrorLabelProps> = (props) => (
  <TextField
    fullWidth
    variant="outlined"
    margin="dense"
    id={`hc-text-${props.name}`}
    {...props}
  />
);

const InputWithErrorLabel: React.FC<InputWithErrorLabelProps> = (props) => {
  const { name, margin = "dense", ...inputProps } = props;
  const label = props.title ?? props.label ?? toProperCase(props.name);
  const [field, meta, helpers] = useField(name);
  // Validate on transition:
  useEffect(() => () => {
    helpers.setTouched(true);
  }, []);

  const helperText = (meta.touched && meta.error) || inputProps.helperText;
  return (
    <TextField
      id={`hc-text-${name}`}
      label={label}
      {...field}
      fullWidth
      variant="outlined"
      margin={margin}
      {...inputProps}
      error={meta.touched && !!meta.error}
      helperText={helperText}
    />
  );
};
export const MaskedInputControl: React.FC<any> = (inputProps) => {
  const other = omit(inputProps, ["inputRef"]);
  return (
    <MaskedInput
      {...other}
      guide={inputProps.guide}
      mask={inputProps.mask}
      placeholderChar={"\u2000"}
    />);
};

const MaskedInputWithErrorLabel: React.FC<InputWithErrorLabelProps & {mask: any, guide?: boolean}> =
(props) => {
  const { name, label, required, margin = "dense" } = props;
  const [field, meta, helpers] = useField(name);
  // Validate on transition:
  useEffect(() => () => {
    helpers.setTouched(true);
  }, []);

  return (
    <FormControl
      fullWidth
      variant="outlined"
      margin={margin}
    >
      <InputLabel htmlFor={`${name}-outlined`} shrink>
        {label}
      </InputLabel>
      <OutlinedInput
        {...field}
        label={label}
        margin={margin === "normal" ? "none" : margin}
        id={`${name}-outlined`}
        inputComponent={MaskedInputControl}
        inputProps={{
          mask: props.mask,
          guide: props.guide,
        }}
        placeholder={props.placeholder}
        required={required}
      />

      {meta.touched && meta.error && (
        <FormHelperText className="color-hc-error">
          {meta.error}
        </FormHelperText>
      )}
    </FormControl>
  );
};


export { Input, InputWithErrorLabel, MaskedInputWithErrorLabel, DatePickerField };
